/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-non-null-assertion */

// A blatant copy of https://github.schibsted.io/smp-distribution/hyperion/blob/master/src/utils/insight/src/insight.ts

const getDomainName = (canonicalUrl: string): string => {
  const domainName = new URL(canonicalUrl).hostname.replace(
    /(.*?\.)(\w+?\.\w+?)$/,
    '$2',
  );
  if (domainName.includes('.') || domainName === 'localhost') {
    return domainName;
  }
  throw new TypeError(`${domainName} is not a valid URL.`);
};

export type DeviceType = 'mobile' | 'tablet' | 'desktop';

const DeviceTypeMap: Record<DeviceType, DeviceType> = {
  desktop: 'desktop',
  tablet: 'tablet',
  mobile: 'mobile',
};

function getDeviceShortName(deviceType?: DeviceType) {
  if (deviceType) {
    return deviceType === DeviceTypeMap.mobile ? 'p' : deviceType.split('')[0];
  }

  return 'u';
}

// Replace underscore and whitespace with dash
const sanitizeUtmTags = (str: string) => str?.replace(/[_\s]/gi, '-');

export interface InsightData {
  source: string;
  medium: string;
  content: string;
  campaign: string;
  tag: string;
}

export interface InsightProps {
  deviceType?: DeviceType;
  query?: any;
  canonicalUrl?: string;
  contentType: InsightContentType;
  contentProvider?: string;
  newsroom?: string;
  contentId?: string;
  hasSalesLeadBody?: boolean;
}

type InsightContentType = 'article' | 'podcast' | 'video' | 'other';

const NO_DATA = 'missing';

interface ReferrerSourceListBuilderConfig {
  pub: {
    prefix: string;
    domain: string;
  };
  variant: {
    frontpage: string | undefined;
    article: string | undefined;
    rQuery: string | undefined;
  };
  // FIXME: type properly
  referrer: any;
}

function buildReferrerSourceList(config: ReferrerSourceListBuilderConfig) {
  const urlRegexPattern = `(http|https)://(www|beta|local)?.?${config.pub.domain.replace(
    /\./g,
    '\\.',
  )}`;
  const { referrer } = config;

  /**
   * An array of posible referrers that we try to match against. The order of the array is important
   * so we can make assumptions in the second element, since it didn't match the first element.
   * The first element of the array is documented, and the rest follow the same format.
   * @type Array'
   */
  return [
    {
      /**
       * The short name of the referrer to be used in the first position of the source in insight.
       * @type String
       */
      shortName: config.pub.prefix,
      /**
       * A predicate function that will return true if the referrer matches
       * @type Function => Boolean
       */
      predicate: () => referrer.search(new RegExp(urlRegexPattern)) > -1,
      /**
       * An optional array for additional info about the referrer. Eg: we know it's from aftenposten.no somewhere,
       * the additional info will try to find out if it came from a front page, article, section page etc.
       * The format of the additional info is exacly the same as the one documented here except it can not
       * have it's own additionalInfo.
       * @type Array
       */

      additionalInfo: [
        {
          // 'w' is widget specifier for frontpage widgets set if 'r' query param is set on links from frontpage
          shortName:
            `f${config.variant.frontpage || ''}` +
            `${config.variant.article ? `w${config.variant.article}` : ''}`,
          predicate: () =>
            referrer.search(new RegExp(`${urlRegexPattern}/?$`)) > -1,
        },
        {
          shortName: `a${config.variant.article || ''}`,
          predicate: () => referrer.search(/\/i\/[a-zA-Z0-9]+\//) > -1,
        },
        {
          shortName: `p1`,
          predicate: () => referrer.search(/restaurantguide/) > -1,
        },
        {
          shortName: 's',
          predicate: () =>
            referrer.search(new RegExp(`${urlRegexPattern}/([a-z-]+)[/]?$`)) >
            -1,
        },
        {
          shortName: 'o',
          predicate: () => true,
        },
      ],
    },
    {
      shortName: 'ap',
      predicate: () =>
        referrer.search(/^(http|https):\/\/www\.aftenposten\.no/) > -1,
      additionalInfo: [
        {
          shortName: 'f',
          predicate: () => referrer.search(/aftenposten\.no\/?(\?|$)/) > -1,
        },
        {
          shortName: 'a',
          predicate: () => referrer.search(/\/i\/[a-zA-Z0-9]+\//) > -1,
        },
      ],
    },
    {
      shortName: 'vink',
      predicate: () => referrer.search(/vink\.aftenposten\.no/) > -1,
      additionalInfo: [
        {
          shortName: 'f',
          predicate: () =>
            referrer.search(/vink\.aftenposten\.no\/?(\?|$)/) > -1,
        },
        {
          shortName: 's',
          predicate: () => referrer.search(/\/mat-og-drikke/) > -1,
        },
        {
          shortName: 's',
          predicate: () => referrer.search(/\/byliv-og-guider/) > -1,
        },
        {
          shortName: 's',
          predicate: () => referrer.search(/\/film-og-serier/) > -1,
        },
        {
          shortName: 'a',
          predicate: () => referrer.search(/\/artikkel\/[a-zA-Z0-9]+\//) > -1,
        },
      ],
    },
    {
      shortName: 'FB',
      predicate: () => referrer.search(/facebook|messenger\.com/) > -1,
      additionalInfo: [
        {
          shortName: 'm',
          predicate: () => referrer.search(/messenger\.com/) > -1,
        },
        {
          shortName: config.variant.rQuery,
          predicate: () => config.variant.rQuery,
        },
        {
          shortName: 'u',
          predicate: () => true,
        },
      ],
    },
    {
      shortName: 'G',
      predicate: () => referrer.search(/google/) > -1,
      additionalInfo: [
        {
          shortName: config.variant.rQuery,
          predicate: () => config.variant.rQuery,
        },
        {
          shortName: 'u',
          predicate: () => true,
        },
      ],
    },
    {
      shortName: 'ig',
      predicate: () => referrer.search(/instagram/) > -1,
      additionalInfo: [
        {
          shortName: config.variant.rQuery,
          predicate: () => config.variant.rQuery,
        },
        {
          shortName: 'u',
          predicate: () => true,
        },
      ],
    },
    {
      shortName: 'Lnkd',
      predicate: () => referrer.search(/(linkedin)|(lnkd.in)/) > -1,
      additionalInfo: [
        {
          shortName: config.variant.rQuery,
          predicate: () => config.variant.rQuery,
        },
        {
          shortName: 'u',
          predicate: () => true,
        },
      ],
    },
    {
      shortName: 'Snap',
      predicate: () => referrer.search(/snapchat/) > -1,
      additionalInfo: [
        {
          shortName: config.variant.rQuery,
          predicate: () => config.variant.rQuery,
        },
        {
          shortName: 'u',
          predicate: () => true,
        },
      ],
    },
    {
      shortName: 'Sol',
      predicate: () => referrer.search(/sol\.no/) > -1,
    },
    {
      shortName: 'Slack',
      predicate: () => referrer.search(/slack/) > -1,
    },
    {
      shortName: 'O',
      predicate: () => true,
    },
  ];
}

export const insight = (
  baseProps: InsightProps,
  salesposterElem: any,
  docRef: string,
): InsightData => {
  const {
    deviceType,
    query: { r: rQuery = '' } = {},
    contentProvider,
    newsroom,
    contentId = NO_DATA,
    contentType,
    canonicalUrl = '',
    hasSalesLeadBody = false,
  } = baseProps;

  const frontpageVariant = (window as any)?.CURATE_CONFIG?.insightVersion;

  const documentReferrer = docRef.split(/[?#]/)[0];
  const referrerList = buildReferrerSourceList({
    pub: {
      prefix: contentProvider || NO_DATA,
      domain: getDomainName(canonicalUrl),
    },
    variant: {
      frontpage: frontpageVariant || '',
      article: rQuery,
      rQuery,
    },
    referrer: documentReferrer,
  });

  const sourceRef = referrerList.filter((r) => r.predicate()).shift()!;

  const additionalInfo = sourceRef.additionalInfo
    ? sourceRef.additionalInfo.filter((w) => w.predicate()).shift()!
    : { shortName: 'u' };

  // NOTE: the `medium` from salesposterElem is different from `medium` in InsightData
  // `salesposterElem.medium` is legacy typo that we still check for. `InsightData.medium`
  // is what we use to track the "medium a user buys via". `salesposterElem.medium` is
  // renamed to `fakseMedium` here, and `InsightData.medium` is `referrerMedium`
  const { campaign, content, medium: fakseMedium } = salesposterElem;
  const salesLeadFlag = hasSalesLeadBody ? '-lead' : '';

  const referrerMedium =
    contentType === 'other'
      ? contentId
      : `${contentType}-${newsroom}-${contentId}`;
  let utm_content;
  let utm_campaign;
  if (window?.history?.state?.utmTags) {
    const utm_tags = window.history.state.utmTags;
    utm_content = utm_tags.content;
    utm_campaign = utm_tags.campaign;
  }

  const data: InsightData = {
    source: `${sourceRef.shortName}-${
      additionalInfo.shortName
    }-${getDeviceShortName(deviceType)}`,
    medium: referrerMedium,
    content:
      (sanitizeUtmTags(utm_content) || content || fakseMedium || NO_DATA) +
      salesLeadFlag,
    campaign: sanitizeUtmTags(utm_campaign) || campaign || NO_DATA,
    tag: NO_DATA,
  };

  data.tag = `${data.source}_${data.medium}_${data.content}_${data.campaign}`;

  return data;
};
